import { useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { LimitNote } from '../components/LimitNote'
import { RichText } from '../components/RichText'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useClubContext, useMaterial, useMaterials, useMaterialsTrash, useSingleFlight } from '../data/hooks'
import { MATERIAL_CATEGORIES, categoryLabel, isCategory, materialMatches, parseTags } from '../data/notes'
import { formatDayShort, parseIsoDate } from '../data/schedule'
import { SliceApiError, type Material, type MaterialCategory, type MaterialInput } from '../data/types'
import { useBackTo } from './AthleteScreen'
import { LockedNote } from './NotesScreens'

/**
 * Материалы направления (этап 5 шаг 3, S20, SC17): требования к аттестации,
 * нормативы, правила - тексты с разделом и тегами. Вход - из «Клуба»: материал
 * принадлежит направлению, а не группе. Поиск и раздел - в адресе, возврат из
 * материала их сохраняет. Штаб читает материалы из той же таблицы на каждом
 * вопросе: созданное, исправленное и удалённое здесь он видит сразу.
 * Материал - общая методичка: править и удалять может любой тренер клуба.
 * Удаление - в корзину на 30 дней (`TrashScreens.tsx`), «Удалённые (N)» -
 * внизу списка.
 */

const ROOT = 'redesign'
const SCOPE = 'Видят и могут менять все тренеры клуба. Штаб опирается на них в ответах.'

const isKind = (e: unknown, kind: SliceApiError['kind']) => e instanceof SliceApiError && e.kind === kind

function useInvalidateMaterials() {
  const client = useQueryClient()
  return async (removedId?: string) => {
    if (removedId) client.removeQueries({ queryKey: [ROOT, 'material', removedId] })
    await client.invalidateQueries({ queryKey: [ROOT, 'materials'] })
    await client.invalidateQueries({ queryKey: [ROOT, 'material'] })
  }
}

/** Первая непустая строка текста - подсказка в списке. */
function preview(content: string): string {
  const line = content
    .split('\n')
    .map((l) => l.replace(/^[-*#\s]+/, '').replace(/\*\*/g, '').trim())
    .find(Boolean)
  if (!line) return ''
  return line.length > 80 ? `${line.slice(0, 80)}…` : line
}

function MaterialRow({ m, showCategory, onOpen }: { m: Material; showCategory: boolean; onOpen: () => void }) {
  const meta = [showCategory ? categoryLabel(m.category) : null, preview(m.content) || null].filter(Boolean).join(' / ')
  return (
    <button type="button" className="pc-list-row" onClick={onOpen}>
      <span className="pc-list-row__main">
        <span className="pc-list-row__title" style={{ display: 'block' }}>
          {m.title}
        </span>
        {meta && <span className="pc-list-row__meta">{meta}</span>}
        {m.tags.length > 0 && <span className="pc-list-row__meta" style={{ display: 'block' }}>Теги: {m.tags.join(', ')}</span>}
      </span>
      <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
    </button>
  )
}

/** S20: список материалов направления с поиском и разделом. */
export function MaterialsScreen() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const back = useBackTo('/club')
  const club = useClubContext()
  const materials = useMaterials()
  const query = params.get('q') ?? ''
  const rawCat = params.get('c')
  const cat: MaterialCategory | null = isCategory(rawCat) ? rawCat : null

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v)
      else p.delete(k)
    }
    setParams(p, { replace: true })
  }

  const direction = club.data?.directionName
  const all = materials.data ?? []
  const found = all.filter((m) => (!cat || m.category === cat) && materialMatches(m, query))
  const filtered = !!cat || !!query.trim()
  const open = (id: string) => navigate(`/club/materials/${id}`)

  let body
  if (materials.isPending) {
    body = <Skeleton rows={3} label="Загружаю материалы" />
  } else if (materials.isError) {
    body = <ErrorState title="Не удалось загрузить материалы" onRetry={() => void materials.refetch()} />
  } else if (all.length === 0) {
    body = (
      <EmptyState
        title="Материалов пока нет"
        text="Сюда кладут требования к аттестации, нормативы и правила - Штаб будет на них опираться."
        action={
          <button type="button" className="pc-btn pc-btn--primary" onClick={() => navigate('/club/materials/new')}>
            Добавить материал
          </button>
        }
      />
    )
  } else if (found.length === 0) {
    const where = [direction ? `материалах «${direction}»` : 'материалах', cat ? `в разделе «${categoryLabel(cat)}»` : null]
      .filter(Boolean)
      .join(' ')
    body = (
      <EmptyState
        title="Ничего не нашлось"
        text={query.trim() ? `В ${where} нет «${query.trim()}».` : `В ${where} пока пусто.`}
        action={
          <button type="button" className="pc-btn" onClick={() => set({ q: null, c: null })}>
            Сбросить поиск
          </button>
        }
      />
    )
  } else if (cat) {
    body = (
      <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 12 }}>
        {found.map((m) => (
          <li key={m.id}>
            <MaterialRow m={m} showCategory={false} onOpen={() => open(m.id)} />
          </li>
        ))}
      </ul>
    )
  } else {
    body = MATERIAL_CATEGORIES.map(([c, label]) => {
      const items = found.filter((m) => m.category === c)
      if (items.length === 0) return null
      return (
        <section key={c} className="pc-section" aria-labelledby={`pc-mat-${c}`}>
          <h2 id={`pc-mat-${c}`} className="pc-section-title">
            {label}
          </h2>
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
            {items.map((m) => (
              <li key={m.id}>
                <MaterialRow m={m} showCategory={false} onOpen={() => open(m.id)} />
              </li>
            ))}
          </ul>
        </section>
      )
    })
  }

  return (
    <Screen
      title="Материалы"
      back={back}
      actionBar={
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          onClick={() => navigate(cat ? `/club/materials/new?c=${cat}` : '/club/materials/new')}
        >
          Добавить материал
        </button>
      }
    >
      <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
        {direction ? `Направление «${direction}». ` : ''}
        {SCOPE}
      </p>
      <div className="pc-search">
        <label className="pc-visually-hidden" htmlFor="pc-material-search">
          Найти материал
        </label>
        <input
          id="pc-material-search"
          className="pc-input"
          type="search"
          placeholder="Найти материал"
          value={query}
          onChange={(e) => set({ q: e.target.value || null })}
          autoComplete="off"
        />
      </div>
      <div className="pc-chips pc-chips--tall" role="group" aria-label="Раздел" style={{ flexWrap: 'wrap' }}>
        <button type="button" aria-pressed={!cat} onClick={() => set({ c: null })}>
          Все
        </button>
        {MATERIAL_CATEGORIES.map(([c, label]) => (
          <button key={c} type="button" aria-pressed={cat === c} onClick={() => set({ c })}>
            {label}
          </button>
        ))}
      </div>
      {filtered && materials.isSuccess && all.length > 0 && (
        <p className="pc-muted" role="status" style={{ margin: '8px 0 0', fontSize: 14 }}>
          Найдено: {found.length} из {all.length}
        </p>
      )}
      {body}
      <MaterialsTrashLink />
    </Screen>
  )
}

/** «Удалённые (N)» внизу списка; пустая корзина - ссылки нет. */
function MaterialsTrashLink() {
  const navigate = useNavigate()
  const bin = useMaterialsTrash()
  if (bin.isPending || (bin.isSuccess && bin.data.length === 0)) return null
  return (
    <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 16 }} onClick={() => navigate('/club/materials/trash')}>
      {bin.isSuccess ? `Удалённые (${bin.data.length})` : 'Удалённые'}
    </button>
  )
}

/** Просмотр материала: текст, раздел, теги; правка и удаление. */
export function MaterialScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const back = useBackTo('/club/materials')
  const material = useMaterial(id)
  const invalidate = useInvalidateMaterials()
  const flight = useSingleFlight()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteError, setDeleteError] = useState<'locked' | 'failed' | null>(null)
  // Удалено: экран уходит. Второе нажатие до ухода не зовёт удаление ещё раз и
  // не уводит «назад» дважды (мимо списка).
  const removed = useRef(false)

  if (material.isPending) {
    return (
      <Screen title="Материал" back={back}>
        <Skeleton rows={3} label="Загружаю материал" />
      </Screen>
    )
  }
  if (material.isError && !material.data) {
    const gone = isKind(material.error, 'not_found')
    return (
      <Screen title="Материал" back={back}>
        {gone ? (
          <EmptyState
            title="Материал не найден"
            text="Возможно, его удалили: удалённые материалы 30 дней лежат в корзине внизу списка материалов."
            action={
              <button type="button" className="pc-btn pc-btn--primary" onClick={() => navigate('/club/materials', { replace: true })}>
                К материалам
              </button>
            }
          />
        ) : (
          <ErrorState title="Не удалось открыть материал" onRetry={() => void material.refetch()} />
        )}
      </Screen>
    )
  }
  const m = material.data
  const updated = parseIsoDate(m.updated_at?.slice(0, 10))

  const remove = async () => {
    if (removed.current) return
    setDeleteError(null)
    try {
      const res = await flight.run(() => api.deleteMaterial(m.id))
      if (res === null) return
      removed.current = true
      setConfirmDelete(false)
      await invalidate(m.id)
      toast(`Материал удалён: ${m.title}. 30 дней его можно вернуть`)
      back()
    } catch (err) {
      // Повтор после потерянного ответа: материала уже нет - это и было нужно.
      if (isKind(err, 'not_found')) {
        removed.current = true
        setConfirmDelete(false)
        await invalidate(m.id)
        toast('Материал уже удалён')
        back()
        return
      }
      setDeleteError(isKind(err, 'limit') ? 'locked' : 'failed')
    }
  }

  return (
    <Screen
      title={m.title}
      back={back}
      actionBar={
        <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={() => navigate(`/club/materials/${m.id}/edit`)}>
          Изменить
        </button>
      }
    >
      <div className="pc-badges">
        <span className="pc-badge">{categoryLabel(m.category)}</span>
        {m.tags.map((t) => (
          <span key={t} className="pc-badge pc-badge--neutral">
            {t}
          </span>
        ))}
      </div>
      <div className="pc-card pc-material-text" style={{ marginTop: 16 }}>
        {m.content.trim() ? <RichText text={m.content} /> : <p className="pc-muted">Текста нет - только название.</p>}
      </div>
      {updated && (
        <p className="pc-field__hint" style={{ marginTop: 8 }}>
          Изменено {formatDayShort(updated)} {updated.getFullYear()}
        </p>
      )}
      <button
        type="button"
        className="pc-btn pc-btn--block pc-btn--danger"
        style={{ marginTop: 24 }}
        onClick={() => {
          setDeleteError(null)
          setConfirmDelete(true)
        }}
      >
        Удалить материал
      </button>

      {confirmDelete && (
        <Sheet title="Удалить материал?" onClose={() => setConfirmDelete(false)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            «{m.title}» попадёт в корзину на 30 дней: вернуть его можно внизу списка материалов, «Удалённые». Штаб перестанет на
            него опираться сразу.
          </p>
          {deleteError === 'locked' ? (
            <LockedNote />
          ) : deleteError ? (
            <SaveFailedAlert>Повторите: если материал уже удалён, повтор это покажет.</SaveFailedAlert>
          ) : null}
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            style={{ marginTop: 12 }}
            disabled={flight.busy}
            onClick={() => void remove()}
          >
            {flight.busy ? 'Удаляю…' : 'Удалить материал'}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setConfirmDelete(false)}>
            Отмена
          </button>
        </Sheet>
      )}
    </Screen>
  )
}

/** Новый материал (`/club/materials/new?c=`) и правка (`/club/materials/:id/edit`). */
export function MaterialFormScreen() {
  const { id } = useParams()
  const navigate = useNavigate()
  const material = useMaterial(id ?? '')
  const title = id ? 'Изменить материал' : 'Новый материал'
  if (id && material.isPending) {
    return (
      <Screen title={title} back={() => navigate(-1)} hideNav>
        <Skeleton rows={3} label="Загружаю материал" />
      </Screen>
    )
  }
  if (id && material.isError && !material.data) {
    const gone = isKind(material.error, 'not_found')
    return (
      <Screen title={title} back={() => navigate(-1)} hideNav>
        {gone ? (
          <EmptyState title="Материал не найден" text="Возможно, его удалили. Материалы удаляются насовсем." />
        ) : (
          <ErrorState title="Не удалось открыть материал" onRetry={() => void material.refetch()} />
        )}
      </Screen>
    )
  }
  return <MaterialForm key={id ?? 'new'} initial={id ? material.data : undefined} />
}

function MaterialForm({ initial }: { initial?: Material }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { api, toast } = useEnv()
  const club = useClubContext()
  const invalidate = useInvalidateMaterials()
  const flight = useSingleFlight()
  const back0 = useBackTo(initial ? `/club/materials/${initial.id}` : '/club/materials')
  const fromFilter = params.get('c')
  const [name, setName] = useState(initial?.title ?? '')
  const [category, setCategory] = useState<MaterialCategory>(initial?.category ?? (isCategory(fromFilter) ? fromFilter : 'general'))
  const [content, setContent] = useState(initial?.content ?? '')
  const [tags, setTags] = useState(initial?.tags.join(', ') ?? '')
  const [nameError, setNameError] = useState<string | undefined>()
  const [saveError, setSaveError] = useState<'locked' | 'gone' | 'failed' | null>(null)
  const [done, setDone] = useState(false)
  // Сохранено: экран уходит, второе нажатие до ухода не создаёт второй материал.
  const saved = useRef(false)

  const snapshot = JSON.stringify([name, category, content, tags])
  const [initialSnapshot] = useState(snapshot)
  const dirty = !done && snapshot !== initialSnapshot
  const leave = useLeaveGuard(dirty && !flight.busy)
  const back = () => leave.guard(back0)

  const input = (): MaterialInput | null => {
    if (!name.trim()) {
      setNameError('Укажите название')
      return null
    }
    setNameError(undefined)
    return { title: name.trim(), content: content.trim(), category, tags: parseTags(tags), sortOrder: initial?.sort_order }
  }

  const save = async (asNew = false) => {
    if (saved.current) return
    const value = input()
    if (!value) return
    setSaveError(null)
    try {
      const editing = initial && !asNew
      const res = await flight.run(() => (editing ? api.updateMaterial(initial.id, value) : api.createMaterial({ ...value, sortOrder: 0 })))
      if (!res) return
      saved.current = true
      setDone(true)
      await invalidate()
      toast(editing ? 'Материал сохранён' : 'Материал добавлен')
      if (editing) back0()
      else navigate(`/club/materials/${res.id}`, { replace: true })
    } catch (err) {
      setSaveError(isKind(err, 'not_found') ? 'gone' : isKind(err, 'limit') ? 'locked' : 'failed')
    }
  }

  return (
    <Screen
      title={initial ? 'Изменить материал' : 'Новый материал'}
      subtitle={club.data?.directionName ? `Направление «${club.data.directionName}»` : undefined}
      back={back}
      hideNav
      actionBar={
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={flight.busy || saveError === 'gone'}
          onClick={() => void save()}
        >
          {flight.busy ? 'Сохраняю…' : initial ? 'Сохранить' : 'Добавить материал'}
        </button>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(ev) => {
          ev.preventDefault()
          void save()
        }}
      >
        {saveError === 'gone' ? (
          <div className="pc-alert" role="alert">
            Материал удалили, пока шла правка: изменения не сохранены. Текст остался на экране.
            <button
              type="button"
              className="pc-btn pc-btn--block"
              style={{ marginTop: 8 }}
              disabled={flight.busy}
              onClick={() => void save(true)}
            >
              {flight.busy ? 'Сохраняю…' : 'Сохранить новым материалом'}
            </button>
          </div>
        ) : saveError === 'locked' ? (
          initial ? (
            <LockedNote guard={leave.guard} />
          ) : (
            <LimitNote what="материалов" reason="locked" />
          )
        ) : saveError ? (
          <SaveFailedAlert>Данные остались на экране, попробуйте ещё раз.</SaveFailedAlert>
        ) : null}
        <Field label="Название" error={nameError}>
          {(p) => (
            <input
              {...p}
              className="pc-input"
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Программа аттестации на 10 кю"
              autoComplete="off"
            />
          )}
        </Field>
        <div className="pc-field">
          <span className="pc-field__label" id="pc-mat-category">
            Раздел
          </span>
          <div className="pc-chips pc-chips--tall" role="group" aria-labelledby="pc-mat-category" style={{ flexWrap: 'wrap', marginTop: 0 }}>
            {MATERIAL_CATEGORIES.map(([c, label]) => (
              <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <Field label="Текст" hint="Списком - строки с дефисом в начале">
          {(p) => <textarea {...p} className="pc-input" rows={8} value={content} onChange={(e) => setContent(e.target.value)} />}
        </Field>
        <Field label="Теги" hint="Через запятую: аттестация, 10 кю">
          {(p) => <input {...p} className="pc-input" value={tags} onChange={(e) => setTags(e.target.value)} autoComplete="off" />}
        </Field>
        <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
          {SCOPE} Название, раздел и теги Штаб видит в каждом разговоре, текст открывает по необходимости.
        </p>
      </form>
      {leave.dialog}
    </Screen>
  )
}
